// ✅ Compact main row + expandable details
// ✅ Moved submitted date, full name, admission date, hostel, active, campus/program fields to details
// ✅ Dynamic documents inside expanded row
// ✅ Education / competitive exams shown like normal fields
// ✅ Expanded details visible from left without horizontal scroll
// ✅ Same orange/blue theme maintained

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Table,
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  Typography,
  Alert,
  Snackbar,
  LinearProgress,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Divider,
  Stack,
  Collapse,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate } from "react-router-dom";
import { API_URL } from "./config";

const THEME_ORANGE = "#F07828";
const THEME_ORANGE_2 = "#ed7f35ff";
const BG = "#F2F2F2";
const BORDER = "rgba(25,118,210,0.20)";
const BORDER_HOVER = "rgba(25,118,210,0.45)";
const BORDER_FOCUS = "rgba(240,120,40,0.80)";
const ROW_ALT = "rgba(25,118,210,0.06)";
const ROW_HOVER = "rgba(25,118,210,0.10)";

const VerifyStudentApplication = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [editedData, setEditedData] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(true);

  const [logincampusCode, setCampusCode] = useState("");
  const [loginusername, setUsername] = useState("");
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);

  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const token = localStorage.getItem("auth");

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    bgcolor: "#fff",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: isSmDown ? "flex-start" : "center",
    justifyContent: "space-between",
    gap: 2,
    flexDirection: isSmDown ? "column" : "row",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER_FOCUS,
    },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const selectSx = {
    ...inputSx,
    "& .MuiSelect-select": { py: 1.05 },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#fff",
    cursor: "pointer",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.75 },
  });

  const fixedGridSx = (cols) => ({
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: `repeat(${Math.min(cols, 2)}, 1fr)`,
      md: `repeat(${Math.min(cols, 4)}, 1fr)`,
      lg: `repeat(${cols}, 1fr)`,
    },
    gap: 1.5,
  });

  useEffect(() => {
    if (!token) return;

    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
      setUsername(decodedToken.username || decodedToken.Username || "");
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("Invalid session token.");
    }
  }, [token]);

  useEffect(() => {
    const fetchLookups = async () => {
      if (!logincampusCode) return;
  
      try {
        const res = await axios.get(`${API_URL}/api/lookups/all`, authHeader);
  
        const lookups = res.data || {};
  
        setAcademicYearOptions(lookups.AcademicYear || []);
        setCurrentAcademicYear(lookups.CurrentAcademicYear?.[0] || "");
      } catch (err) {
        console.error("Failed to load academic years:", err);
        setAcademicYearOptions([]);
        setCurrentAcademicYear("");
      }
    };
  
    fetchLookups();
  }, [logincampusCode, authHeader]);

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      if (!logincampusCode) return;

      try {
        const res = await axios.get(`${API_URL}/api/document-types`, authHeader);
        setDocumentTypeOptions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error("Failed to load document types:", e);
        setDocumentTypeOptions([]);
      }
    };

    fetchDocumentTypes();
  }, [logincampusCode, authHeader]);

  useEffect(() => {
    const fetchData = async () => {
      if (!logincampusCode) return;

      setError("");
      setLoading(true);

      try {
        const response = await axios.get(
          `${API_URL}/api/VerifyStudentApplication`,
          authHeader
        );
        setData(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching data:", err);
        if (err?.response?.status === 401) navigate("/");
        else setError("Failed to load student applications.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [logincampusCode, navigate, authHeader]);

  useEffect(() => {
    const fetchSemesters = async () => {
      if (!logincampusCode) return;

      try {
        const res = await axios.get(`${API_URL}/api/getSemesters`, {
          ...authHeader,
          params: { CampusCode: logincampusCode },
        });

        const rows = Array.isArray(res.data) ? res.data : [];

        setSemesterOptions(
          rows.map((s) => {
            const value =
              s.Semester ?? s.SemesterNumber ?? s.SemesterID ?? s.Value;
            const label = s.SemesterName ?? String(value);
            return { value, label };
          })
        );
      } catch (e) {
        console.error("Failed to load semesters", e);
        setSemesterOptions([]);
      }
    };

    fetchSemesters();
  }, [logincampusCode, authHeader]);

  const formatDateOnly = (value) => {
    if (!value) return "";
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const iso = str.match(/^(\d{4}-\d{2}-\d{2})T/);
    if (iso) return iso[1];
    return str;
  };

  const handleFieldChange = (ApplicationID, field, value) => {
    setEditedData((prev) => {
      const nextForRow = {
        ...prev[ApplicationID],
        [field]:
          field === "IsStudentActive" ? (value === "Yes" ? "Yes" : "No") : value,
      };

      if (field === "Entry" && value === "Regular" && semesterOptions.length) {
        nextForRow.Semester = semesterOptions[0].value;
      }

      return { ...prev, [ApplicationID]: nextForRow };
    });
  };

  const getEditedValue = (item, field, fallback = "") =>
    editedData[item.ApplicationID]?.[field] ?? item[field] ?? fallback;

  const getRowDocs = (ApplicationID, originalDocs = []) =>
    editedData[ApplicationID]?.DocumentDetails ?? originalDocs ?? [];

  const handleDocumentChange = (ApplicationID, index, field, value) => {
    setEditedData((prev) => {
      const original = data.find((x) => x.ApplicationID === ApplicationID);
      const docs = [
        ...(prev[ApplicationID]?.DocumentDetails ??
          original?.DocumentDetails ??
          []),
      ];

      docs[index] = { ...docs[index], [field]: value };

      return {
        ...prev,
        [ApplicationID]: {
          ...prev[ApplicationID],
          DocumentDetails: docs,
        },
      };
    });
  };

  const handleAddDocument = (ApplicationID) => {
    setEditedData((prev) => {
      const original = data.find((x) => x.ApplicationID === ApplicationID);
      const docs = [
        ...(prev[ApplicationID]?.DocumentDetails ??
          original?.DocumentDetails ??
          []),
        {
          DocumentType: "",
          IsReceived: false,
          IsVerified: false,
          Comments: "",
        },
      ];

      return {
        ...prev,
        [ApplicationID]: {
          ...prev[ApplicationID],
          DocumentDetails: docs,
        },
      };
    });

    setExpandedRows({ [ApplicationID]: true });
  };

  const handleRemoveDocument = (ApplicationID, index) => {
    setEditedData((prev) => {
      const original = data.find((x) => x.ApplicationID === ApplicationID);
      const docs = [
        ...(prev[ApplicationID]?.DocumentDetails ??
          original?.DocumentDetails ??
          []),
      ].filter((_, i) => i !== index);

      return {
        ...prev,
        [ApplicationID]: {
          ...prev[ApplicationID],
          DocumentDetails: docs,
        },
      };
    });
  };

  const renderField = (label, value) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Box>
  );

  const renderDocumentsCellSummary = (item) => {
    const docs = getRowDocs(item.ApplicationID, item.DocumentDetails);

    if (!docs.length) {
      return <Chip size="small" label="No documents" />;
    }

    const verified = docs.filter((d) => d.IsVerified).length;

    return (
      <Stack spacing={0.5}>
        <Chip
          size="small"
          label={`${docs.length} document(s)`}
          sx={{ bgcolor: "rgba(25,118,210,0.08)", fontWeight: 800 }}
        />
        <Typography variant="caption" color="text.secondary">
          Verified: {verified}/{docs.length}
        </Typography>
      </Stack>
    );
  };

  const renderDocumentEditor = (item) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderColor: BORDER,
        bgcolor: "#FBFDFF",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
            Documents
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add any document type and mark received / verified.
          </Typography>
        </Box>

        <Button
          size="small"
          variant="contained"
          sx={primaryBtnSx}
          onClick={(e) => {
            e.stopPropagation();
            handleAddDocument(item.ApplicationID);
          }}
        >
          Add Document
        </Button>
      </Stack>

      <Stack spacing={1}>
        {getRowDocs(item.ApplicationID, item.DocumentDetails).length === 0 && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No documents added. Click Add Document.
          </Alert>
        )}

        {getRowDocs(item.ApplicationID, item.DocumentDetails).map(
          (doc, docIdx) => (
            <Paper
              key={`${item.ApplicationID}-doc-${docIdx}`}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderColor: BORDER,
                bgcolor: "#fff",
              }}
            >
              <Stack spacing={1}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={documentTypeOptions}
                  value={doc.DocumentType || ""}
                  onInputChange={(_, value) => {
                    handleDocumentChange(
                      item.ApplicationID,
                      docIdx,
                      "DocumentType",
                      value
                    );
                  }}
                  onChange={async (_, value) => {
                    const documentType = typeof value === "string" ? value : value || "";
                  
                    handleDocumentChange(
                      item.ApplicationID,
                      docIdx,
                      "DocumentType",
                      documentType
                    );
                  
                    await saveDocumentTypeIfNeeded(documentType);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Document Type"
                      size="small"
                      sx={inputSx}
                      placeholder="Type or select document type"
                      onBlur={async (e) => {
                        await saveDocumentTypeIfNeeded(e.target.value);
                      }}
                    />
                  )}
                />

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!doc.IsReceived}
                        onChange={(e) =>
                          handleDocumentChange(
                            item.ApplicationID,
                            docIdx,
                            "IsReceived",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Received"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!doc.IsVerified}
                        onChange={(e) =>
                          handleDocumentChange(
                            item.ApplicationID,
                            docIdx,
                            "IsVerified",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Verified"
                  />

                  <Button
                    size="small"
                    color="error"
                    onClick={() =>
                      handleRemoveDocument(item.ApplicationID, docIdx)
                    }
                    sx={{ textTransform: "none", ml: { sm: "auto" } }}
                  >
                    Remove
                  </Button>
                </Stack>

                <TextField
                  size="small"
                  fullWidth
                  label="Document Comments"
                  sx={inputSx}
                  value={doc.Comments || ""}
                  onChange={(e) =>
                    handleDocumentChange(
                      item.ApplicationID,
                      docIdx,
                      "Comments",
                      e.target.value
                    )
                  }
                />
              </Stack>
            </Paper>
          )
        )}
      </Stack>
    </Paper>
  );

  const renderEducationCards = (rows = []) => {
    if (!rows.length) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No education details found.
        </Alert>
      );
    }

    return (
      <Stack spacing={1.5}>
        {rows.map((edu, idx) => (
          <Paper
            key={`edu-${idx}`}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, borderColor: BORDER }}
          >

            <Box sx={fixedGridSx(5)}>
              {renderField("Level", edu.EducationLevel)}
              {renderField("Board Type", edu.BoardType)}
              {renderField("Institution", edu.InstitutionName)}
              {renderField("Hall Ticket", edu.HallTicketNumber)}
              {renderField("Percentage", edu.Percentage)}
              {renderField("CGPA", edu.CGPA)}
              {renderField("City", edu.InstitutionCity)}
              {renderField("State", edu.InstitutionState)}
              {renderField("Passed Out Year", edu.PassedOutYear)}
            </Box>
          </Paper>
        ))}
      </Stack>
    );
  };

  const renderCompetitiveExamCards = (rows = []) => {
    if (!rows.length) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No competitive exam details found.
        </Alert>
      );
    }

    return (
      <Stack spacing={1.5}>
        {rows.map((exam, idx) => (
          <Paper
            key={`exam-${idx}`}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, borderColor: BORDER }}
          >

            <Box sx={fixedGridSx(5)}>
              {renderField("Exam Type", exam.ExamType)}
              {renderField("Hall Ticket", exam.HallTicketNumber)}
              {renderField("Rank", exam.RankNo)}
              {renderField("Score", exam.Score)}
              {renderField("Year", exam.ExamYear)}
            </Box>
          </Paper>
        ))}
      </Stack>
    );
  };

  const handleSave = async (ApplicationID) => {
    const updatedFields = editedData[ApplicationID] || {};
    const originalData = data.find((item) => item.ApplicationID === ApplicationID);

    const docs =
      updatedFields.DocumentDetails ?? originalData?.DocumentDetails ?? [];
    const cleanedDocs = docs.filter((d) => String(d.DocumentType || "").trim());

    if (cleanedDocs.length === 0) {
      alert("Please add at least one document type.");
      return;
    }

    const updatedData = {
      ...originalData,
      ...updatedFields,
      DocumentDetails: cleanedDocs,
      DocumentsVerified:
        updatedFields.DocumentsVerified ??
        originalData?.DocumentsVerified ??
        "Pending",
      loginusername,
    };

    try {
      setSavingId(ApplicationID);
      setError("");

      const response = await axios.post(
        `${API_URL}/api/updateVerifyStudentApplication`,
        updatedData,
        authHeader
      );

    if (response.status === 200) {
      setSuccess(true);
    
      setData((prev) =>
        prev.map((row) =>
          row.ApplicationID === ApplicationID
            ? {
                ...row,
                ...updatedFields,
                DocumentDetails: cleanedDocs,
                // ✅ Do NOT auto-set this anymore
                DocumentsVerified:
                  updatedFields.DocumentsVerified ?? row.DocumentsVerified ?? "Pending",
              }
            : row
        )
      );
    
      setEditedData((prev) => {
        const { [ApplicationID]: _removed, ...rest } = prev;
        return rest;
      });
    }
    } catch (err) {
      console.error("Error updating application:", err);
      setError("An error occurred while updating the application.");
    } finally {
      setSavingId(null);
    }
  };

  const saveDocumentTypeIfNeeded = async (value) => {
    const documentType = String(value || "").trim();
    if (!documentType) return;

    const exists = documentTypeOptions.some(
      (x) => String(x).toLowerCase() === documentType.toLowerCase()
    );

    if (exists) return;

    try {
      await axios.post(
        `${API_URL}/api/document-types`,
        { DocumentType: documentType },
        authHeader
      );

      setDocumentTypeOptions((prev) =>
        Array.from(new Set([...prev, documentType])).sort((a, b) =>
          a.localeCompare(b)
        )
      );
    } catch (err) {
      console.error("Failed to save document type:", err);
    }
  };

  const expandedItem = data.find((x) => expandedRows[x.ApplicationID]);

  const manualDocsVerified =
    expandedItem &&
    (editedData[expandedItem.ApplicationID]?.DocumentsVerified ||
      expandedItem.DocumentsVerified ||
      "Pending");

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageWrapSx}>
        <Paper elevation={5} sx={rootPaperSx}>
          <Box sx={headerSx}>
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
                Verify Student Application
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
                Click a row to expand details and verify documents
              </Typography>
            </Box>

            {loading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} sx={{ color: "white" }} />
                <Typography sx={{ fontSize: "0.85rem" }}>Loading…</Typography>
              </Stack>
            )}
          </Box>

          <Box sx={bodySx}>
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Snackbar
              open={success}
              autoHideDuration={2500}
              onClose={() => setSuccess(false)}
            >
              <Alert
                severity="success"
                sx={{ borderRadius: 2 }}
                onClose={() => setSuccess(false)}
              >
                Updated successfully!
              </Alert>
            </Snackbar>

            {loading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
              </Box>
            )}

            <Paper variant="outlined" sx={sectionSx}>
              <Box sx={{ pl: 1 }}>
                <Typography
                  sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}
                >
                  Applications
                </Typography>
                <Paper elevation={0} sx={tableWrapSx}>
                  <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                    <TableContainer sx={surfaceSx}>
                      {loading ? (
                        <Typography sx={{ textAlign: "center", py: 2 }}>
                          Loading...
                        </Typography>
                      ) : data.length === 0 ? (
                        <Typography sx={{ textAlign: "center", py: 2 }}>
                          No records found
                        </Typography>
                      ) : (
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={theadRowSx}>
                              {[
                                "",
                                "Application ID",
                                "Entry",
                                "Academic Year",
                                "Semester",
                                "Qualification",
                                "Admission Type",
                                "Documents",
                                "Docs Verified",
                                "Docs Stored Info",
                                "Comments",
                                "App Status",
                                "Actions",
                              ].map((h) => (
                                <TableCell key={h}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data.map((item, idx) => {
                              const isExpanded = !!expandedRows[item.ApplicationID];
                              const semValue =
                                editedData[item.ApplicationID]?.Semester ??
                                item.Semester ??
                                (semesterOptions[0]?.value ?? "");
                              return (
                                <React.Fragment key={item.ApplicationID}>
                                  <TableRow
                                    sx={zebraRowSx(idx)}
                                    onClick={() =>
                                       setExpandedRows((prev) => {
                                         const currentlyOpen = !!prev[item.ApplicationID];
                                         return currentlyOpen ? {} : { [item.ApplicationID]: true };
                                       })
                                    }
                                  >
                                    <TableCell>{isExpanded ? "▾" : "▸"}</TableCell>
                                    <TableCell>{item.ApplicationID}</TableCell>
                                    <TableCell
                                      sx={{ minWidth: 130 }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Select
                                        size="small"
                                        fullWidth
                                        sx={selectSx}
                                        value={getEditedValue(item, "Entry")}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            item.ApplicationID,
                                            "Entry",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="Regular">Regular</MenuItem>
                                        <MenuItem value="Lateral">Lateral</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                      </Select>
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 160 }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Select
                                        size="small"
                                        fullWidth
                                        sx={selectSx}
                                        value={getEditedValue(item, "AcademicYear")}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            item.ApplicationID,
                                            "AcademicYear",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="">
                                          Select
                                        </MenuItem>
                                      
                                        {academicYearOptions.map((year) => (
                                          <MenuItem key={year} value={year}>
                                            {year}
                                            {year === currentAcademicYear ? " Current" : ""}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 140 }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Select
                                        size="small"
                                        fullWidth
                                        sx={selectSx}
                                        value={semValue}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            item.ApplicationID,
                                            "Semester",
                                            e.target.value
                                          )
                                        }
                                      >
                                        {semesterOptions.map((opt) => (
                                          <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 135 }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Select
                                        size="small"
                                        fullWidth
                                        sx={selectSx}
                                        value={getEditedValue(item, "Qualification")}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            item.ApplicationID,
                                            "Qualification",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="Tenth">Tenth</MenuItem>
                                        <MenuItem value="Inter">Inter</MenuItem>
                                        <MenuItem value="Degree">Degree</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                      </Select>
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 150 }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Select
                                        size="small"
                                        fullWidth
                                        sx={selectSx}
                                        value={getEditedValue(item, "AdmissionType")}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            item.ApplicationID,
                                            "AdmissionType",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="Councelling">
                                          Councelling
                                        </MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                      </Select>
                                    </TableCell>
                                    <TableCell>
                                      {renderDocumentsCellSummary(item)}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 140 }} onClick={(e) => e.stopPropagation()}>
                                      <Select
                                        size="small"
                                        fullWidth
                                        sx={selectSx}
                                        value={
                                          editedData[item.ApplicationID]?.DocumentsVerified ||
                                          item.DocumentsVerified ||
                                          "Pending"
                                        }
                                        onChange={(e) =>
                                          handleFieldChange(
                                            item.ApplicationID,
                                            "DocumentsVerified",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="Pending">Pending</MenuItem>
                                        <MenuItem value="Verified">Verified</MenuItem>
                                      </Select>
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 180 }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <TextField
                                        size="small"
                                        fullWidth
                                        sx={inputSx}
                                        value={getEditedValue(
                                          item,
                                          "DocumentsStoredInfo"
                                        )}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            item.ApplicationID,
                                            "DocumentsStoredInfo",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 180 }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <TextField
                                        size="small"
                                        fullWidth
                                        sx={inputSx}
                                        value={getEditedValue(item, "Comments")}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            item.ApplicationID,
                                            "Comments",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>{item.ApplicationStatus}</TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                      <Button
                                        variant="contained"
                                        sx={primaryBtnSx}
                                        onClick={() =>
                                          handleSave(item.ApplicationID)
                                        }
                                        disabled={
                                          !editedData[item.ApplicationID] ||
                                          savingId === item.ApplicationID
                                        }
                                      >
                                        {savingId === item.ApplicationID ? (
                                          <CircularProgress
                                            size={18}
                                            sx={{ color: "white" }}
                                          />
                                        ) : (
                                          "Save"
                                        )}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                      
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </TableContainer>
                  </Box>
                </Paper>
                {expandedItem && (
                  <Box sx={{ mt: 2 }}>
                    <Collapse in timeout="auto">
                      <Stack spacing={2}>
                             
                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Application & Student Details
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={fixedGridSx(5)}>
                              {renderField("Application ID", expandedItem.ApplicationID)}
                              {renderField("Full Name", expandedItem.FullName)}
                              {renderField("Submitted Date", formatDateOnly(expandedItem.ApplicationSubmittedDateTime))}
                              {renderField("Application Status", expandedItem.ApplicationStatus)}
                              <Box onClick={(e) => e.stopPropagation()}>
                                <Typography variant="caption" color="text.secondary">
                                  Admission Date
                                </Typography>
                                <DatePicker
                                  format="YYYY-MM-DD"
                                  value={
                                    getEditedValue(expandedItem, "AdmissionDate")
                                      ? dayjs(formatDateOnly(getEditedValue(expandedItem, "AdmissionDate")))
                                      : null
                                  }
                                  onChange={(newValue) =>
                                    handleFieldChange(
                                      expandedItem.ApplicationID,
                                      "AdmissionDate",
                                      newValue ? newValue.format("YYYY-MM-DD") : ""
                                    )
                                  }
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      fullWidth: true,
                                      sx: inputSx,
                                    },
                                  }}
                                />
                              </Box>
                                
                                
                              {renderField("Campus Code", expandedItem.CampusCode)}
                              {renderField("SubCampus Code", expandedItem.SubCampusCode)}
                              {renderField("Degree Code", expandedItem.DegreeCode)}
                              {renderField("Branch Code", expandedItem.BranchCode)}
                              {renderField("Gender", expandedItem.Gender)}
                              {renderField("DOB", formatDateOnly(expandedItem.DateOfBirth))}
                              {renderField("Blood Group", expandedItem.BloodGroup)}
                              {renderField("Aadhar", expandedItem.AadharNumber)}
                              {renderField("Phone", expandedItem.PhoneNumber)}
                              {renderField("Email", expandedItem.EmailAddress)}
                            </Box>
                          </Box>
                        </Paper>
                              
                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Education Details
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {renderEducationCards(expandedItem.EducationDetails || [])}
                            </Box>
                        </Paper>
                              
                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Competitive / Entrance Exams
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {renderCompetitiveExamCards(expandedItem.CompetitiveExams || [])}
                          </Box>
                        </Paper>

                        {renderDocumentEditor(expandedItem)}

                      </Stack>
                    </Collapse>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default VerifyStudentApplication;